import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import Stack from '@mui/joy/Stack';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import List from '@mui/joy/List';
import {
  Box, Button, Chip, IconButton, Input, ListItem, ModalOverflow,
  CircularProgress, Modal, ModalDialog, Tooltip, useTheme,
} from '@mui/joy';
import useMediaQuery from '@mui/material/useMediaQuery';

import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import GroupAddRoundedIcon from '@mui/icons-material/GroupAddRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import CreateNewFolderRoundedIcon from '@mui/icons-material/CreateNewFolderRounded';
import PushPinRoundedIcon from '@mui/icons-material/PushPinRounded';

import ChatListItem from './ChatListItem';
import CreateGroupModal from './CreateGroupModal';
import AvatarWithStatus from './AvatarWithStatus';
import ChatContextMenu from './ChatContextMenu';
import FolderStrip from './FolderStrip';
import FolderModal from './FolderModal';
import {
  APIGetChats, APICreateChatWithEmployee, APIGetEmployeesWithoutDM,
  APIGetChatFolders, APICreateChatFolder, APIUpdateChatFolder, APIDeleteChatFolder,
  APIFolderToggleChat, APIToggleChatPin, APILeaveChat, APIMarkChatReadAll, APIReorderChatFolders,
  APIChatsPresenceBulk,
} from '../../api';
import { LayoutContext } from '../../context';
import { MOTION, ellipsis, thinScrollbar, iconColor } from './chatTheme';

function getPositionLabel(position) {
  if (!Array.isArray(position)) return '—';
  const labels = [];
  if (position.includes('manager')) labels.push('Менеджер');
  if (position.includes('head_manager')) labels.push('Старший менеджер');
  if (position.includes('surveyor')) labels.push('Обмерщик');
  if (position.includes('draftsman')) labels.push('Чертежник');
  if (position.includes('quality_master')) labels.push('Мастер качества');
  return labels.join(', ') || '—';
}

function sharedChatsLabel(n) {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `В ${n} общем чате`;
  return `В ${n} общих чатах`;
}

// ── Строка контакта ───────────────────────────────────────────────────
function ContactRow({ contact, busy, onAction }) {
  const hasDm = contact.has_dm && contact.chat_id;
  const shared = Number(contact.shared_chats || 0);
  const fullName = `${contact.last_name || ''} ${contact.name || ''}`.trim();

  return (
    <ListItem sx={{ p: 0 }}>
      <Box
        sx={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 1.25,
          px: 1, py: 0.875, borderRadius: '14px', minWidth: 0,
          transition: 'background-color .15s',
          '&:hover': { backgroundColor: 'background.level1' },
        }}
      >
        <AvatarWithStatus size="md" name={fullName} person={contact} />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography level="title-sm" sx={{ ...ellipsis, fontWeight: 650 }}>
            {fullName || 'Сотрудник'}
          </Typography>
          <Typography level="body-xs" sx={{ ...ellipsis, color: 'text.tertiary' }}>
            {getPositionLabel(contact.position)}
          </Typography>
          {shared > 0 && (
            <Chip
              size="sm" variant="soft" color="neutral"
              startDecorator={<GroupsRoundedIcon sx={{ fontSize: 13 }} />}
              sx={{ mt: 0.375, borderRadius: '8px', fontSize: 11 }}
            >
              {sharedChatsLabel(shared)}
            </Chip>
          )}
        </Box>

        <Button
          size="sm"
          variant={hasDm ? 'soft' : 'solid'}
          color="primary"
          loading={busy}
          startDecorator={hasDm ? <ChatRoundedIcon sx={{ fontSize: 16 }} /> : <EditNoteRoundedIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => { e.stopPropagation(); onAction(contact); }}
          sx={{ flexShrink: 0, borderRadius: '12px', minWidth: 108, fontWeight: 600, fontSize: 13 }}
        >
          {hasDm ? 'Открыть' : 'Написать'}
        </Button>
      </Box>
    </ListItem>
  );
}

// ── Модалка «Контакты» ────────────────────────────────────────────────
function ContactsModal({ open, onClose, contacts, loading, busyId, onAction }) {
  const [searchTerm, setSearchTerm] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => { if (open) setSearchTerm(''); }, [open]);

  const filtered = contacts.filter((emp) => {
    const fullName = `${emp.last_name || ''} ${emp.name || ''}`.toLowerCase();
    return fullName.includes(searchTerm.toLowerCase());
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ zIndex: 10012 }}
      slotProps={{
        backdrop: {
          component: motion.div,
          initial: { opacity: 0, backdropFilter: 'blur(0px)' },
          animate: { opacity: 1, backdropFilter: 'blur(10px)' },
          exit: { opacity: 0, backdropFilter: 'blur(0px)' },
          transition: { duration: MOTION.base, ease: 'easeInOut' },
        },
      }}
    >
      <ModalOverflow sx={{ height: 'auto' }}>
        <ModalDialog
          layout={isMobile ? 'fullscreen' : 'center'}
          size="md"
          component={motion.div}
          initial={{ opacity: 0, y: 18, scale: 0.97 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 18, scale: 0.97 }}
          transition={MOTION.softSpring}
          sx={{
            width: isMobile ? '100%' : 440,
            maxHeight: isMobile ? '100dvh' : '72vh',
            p: 0, borderRadius: { xs: 0, sm: '22px' }, overflow: 'hidden',
            display: 'flex', flexDirection: 'column',
            // На десктопе центрируем флексом ModalOverflow, а не собственным
            // translate(-50%,-50%) Joy: framer-motion пишет свой transform из
            // animate и это translate затирает. На мобиле layout="fullscreen",
            // там позиционирование через inset и трогать его нельзя.
            ...(isMobile ? null : { position: 'relative', top: 'auto', left: 'auto', my: 'auto' }),
          }}
        >
          <Stack
            direction="row" alignItems="center" justifyContent="space-between"
            sx={{ px: 2.5, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}
          >
            <Typography level="title-md" sx={{ fontWeight: 750 }}>Контакты</Typography>
            <IconButton variant="plain" size="sm" onClick={onClose} sx={{ borderRadius: '50%' }}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>

          <Box sx={{ px: 2, py: 1.5 }}>
            <Input
              placeholder="Поиск сотрудника…"
              startDecorator={<SearchRoundedIcon sx={{ fontSize: 18 }} />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              sx={{ borderRadius: '14px' }}
            />
          </Box>

          <Box sx={(t) => ({ flex: 1, overflow: 'auto', px: 1.5, pb: 1.5, ...thinScrollbar(t) })}>
            {loading ? (
              <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress /></Stack>
            ) : (
              <List sx={{ '--List-gap': '2px', p: 0 }}>
                {filtered.map((c) => (
                  <ContactRow key={c.id} contact={c} busy={busyId === c.id} onAction={onAction} />
                ))}
              </List>
            )}
            {!loading && filtered.length === 0 && (
              <Typography level="body-sm" sx={{ textAlign: 'center', py: 4, color: 'text.tertiary' }}>
                Сотрудники не найдены
              </Typography>
            )}
          </Box>
        </ModalDialog>
      </ModalOverflow>
    </Modal>
  );
}

// ── Панель со списком чатов ───────────────────────────────────────────
export default function ChatsPane(props) {
  const { chats, setChats, selectedChatId, handleChatSelect, onNewChatCreated } = props;
  const { tokenEmployee, loadsUnreadCounts } = useContext(LayoutContext);

  const [contactsModalOpen, setContactsModalOpen] = useState(false);
  const [groupModalOpen, setGroupModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [busyContactId, setBusyContactId] = useState(null);
  const openingContactRef = useRef(false);

  const CHATS_PAGE = 20;
  const [chatsLoading, setChatsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [chatsOffset, setChatsOffset] = useState(0);
  const [chatsTotal, setChatsTotal] = useState(0);
  const [hasMoreChats, setHasMoreChats] = useState(false);

  // ── Папки ──────────────────────────────────────────────────────────
  const [folders, setFolders] = useState([]);
  const [folderRules, setFolderRules] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);   // null — «Все»
  // Та же папка, но читаемая из асинхронных обработчиков: состояние там
  // всегда «как было на момент запуска», а нужно «как сейчас».
  const activeFolderRef = useRef(null);

  // ── Кэш списков по папкам ──────────────────────────────────────────
  // Ключ — 'all' или id папки. Нужен, чтобы повторный заход в папку был
  // мгновенным: список показываем сразу из кэша, а свежий подтягиваем
  // молча и подменяем. Человек листает вкладки быстрее, чем отвечает
  // сервер, и ждать ответа ради уже виденного списка незачем.
  const folderCacheRef = useRef(new Map());
  const folderKey = (id) => String(id ?? 'all');
  const [allUnread, setAllUnread] = useState(0);
  const [folderModal, setFolderModal] = useState({ open: false, chat: null });
  const [folderBusy, setFolderBusy] = useState(false);
  const foldersRef = useRef([]);
  const folderLoadRef = useRef(0);
  const chatRefreshRef = useRef(0);
  const folderTokenRef = useRef(tokenEmployee);
  folderTokenRef.current = tokenEmployee;
  // Направление перелистывания: вкладка правее — лента уезжает влево.
  const [chatMenu, setChatMenu] = useState(null);           // { anchor, chat }

  const loadFolders = async () => {
    if (!tokenEmployee) return;
    const session = tokenEmployee;
    const requestId = ++folderLoadRef.current;
    try {
      const data = await APIGetChatFolders(session);
      if (folderTokenRef.current !== session || requestId !== folderLoadRef.current) return;
      if (data?.status === 'ok') {
        foldersRef.current = data.folders || [];
        setFolders(foldersRef.current);
        setFolderRules(data.rules || []);
        setAllUnread(data.all_unread_count || 0);
      }
    } catch (error) {
      console.error('Ошибка загрузки папок:', error);
    }
  };

  useEffect(() => {
    foldersRef.current = [];
    setFolders([]);
    setFolderBusy(false);
    setActiveFolder(null);
    activeFolderRef.current = null;
    chatRefreshRef.current += 1;
    folderCacheRef.current.clear();
    loadFolders();
    return () => { folderLoadRef.current += 1; };
  }, [tokenEmployee]);
  useEffect(() => {
    let timer = null;
    let disposed = false;
    const session = tokenEmployee;
    const sync = () => {
      folderCacheRef.current.clear();
      clearTimeout(timer);
      timer = setTimeout(async () => {
        await loadFolders();
        if (disposed || folderTokenRef.current !== session) return;
        const id = activeFolderRef.current;
        const current = foldersRef.current.find(folder => String(folder.id) === String(id));
        if (current?.auto_rules?.some(rule => rule === 'unread' || rule === 'mentions')) {
          await refreshChatsQuiet(id, { syncUnread: false });
        }
      }, 250);
    };
    window.addEventListener('planium:unread-synced', sync);
    return () => { disposed = true; clearTimeout(timer); window.removeEventListener('planium:unread-synced', sync); };
  }, [tokenEmployee]);

  useEffect(() => {
    const session = tokenEmployee;
    const revision = chatRefreshRef.current;
    const _load = async () => {
      try {
        setChatsLoading(true);
        const data = await APIGetChats(session, { limit: CHATS_PAGE, offset: 0 });
        if (folderTokenRef.current !== session || revision !== chatRefreshRef.current || activeFolderRef.current !== null) return;
        const list = data?.chats || [];
        setChats(list);
        setChatsOffset(list.length);
        setChatsTotal(data?.total ?? list.length);
        setHasMoreChats(data?.has_more ?? false);
        // Кладём «Все» в кэш сразу: возврат на эту вкладку — самый частый
        // переход, и он должен быть мгновенным с первой же секунды.
        folderCacheRef.current.set('all', {
          list,
          offset: list.length,
          total: data?.total ?? list.length,
          hasMore: data?.has_more ?? false,
        });
      } catch (error) {
        console.error('Ошибка загрузки чатов:', error);
      } finally {
        if (folderTokenRef.current === session) setChatsLoading(false);
      }
    };
    setChats([]);
    setChatsOffset(0);
    setChatsTotal(0);
    setHasMoreChats(false);
    if (session) _load(); else setChatsLoading(false);
  }, [tokenEmployee]);

  const loadMoreChats = async () => {
    if (loadingMore || !hasMoreChats || searchQuery.trim()) return;
    // Папка, за которой пошли. Пока ответ едет, человек вполне может
    // переключить вкладку — и дописывать её списку чужую страницу нельзя.
    const forFolder = activeFolderRef.current;
    const session = tokenEmployee;
    const revision = chatRefreshRef.current;
    try {
      setLoadingMore(true);
      const data = await APIGetChats(tokenEmployee, {
        limit: CHATS_PAGE, offset: chatsOffset, folder: forFolder,
      });
      if (folderTokenRef.current !== session || revision !== chatRefreshRef.current
        || folderKey(forFolder) !== folderKey(activeFolderRef.current)) return;
      if (!Array.isArray(data?.chats)) return;
      const list = data.chats;
      let merged = null;
      setChats((prev) => {
        const seen = new Set(prev.map((c) => c.chat_id));
        merged = [...prev, ...list.filter((c) => !seen.has(c.chat_id))];
        return merged;
      });
      // Смещение — по ФАКТИЧЕСКОЙ длине списка, а не «старое плюс сколько
      // пришло». Дубли отфильтровываются, и прежний счёт уползал вперёд:
      // следующая страница запрашивалась с пропуском, и часть чатов не
      // показывалась никогда.
      setChatsOffset((o) => (merged ? merged.length : o + list.length));
      setChatsTotal(data?.total ?? chatsTotal);
      setHasMoreChats(data?.has_more ?? false);
      // Кэш папки держим в актуальном виде, иначе возврат в неё показал бы
      // только первую страницу и снова заставил долистывать.
      const snap = folderCacheRef.current.get(folderKey(forFolder));
      if (snap && merged) {
        folderCacheRef.current.set(folderKey(forFolder), {
          list: merged,
          offset: merged.length,
          total: data?.total ?? snap.total,
          hasMore: data?.has_more ?? false,
        });
      }
    } catch (error) {
      console.error('Ошибка догрузки чатов:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleChatsScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 240) loadMoreChats();
  };

  const loadContacts = async () => {
    if (!tokenEmployee) return;
    const session = tokenEmployee;
    try {
      setContactsLoading(true);
      const data = await APIGetEmployeesWithoutDM(tokenEmployee);
      if (folderTokenRef.current === session) setContacts(data?.employees || []);
    } catch (error) {
      console.error('Ошибка загрузки контактов:', error);
    } finally {
      setContactsLoading(false);
    }
  };

  useEffect(() => { setContacts([]); if (tokenEmployee) loadContacts(); }, [tokenEmployee]);

  const sortedChats = useMemo(() => {
    if (!chats || chats.length === 0) return [];
    let filtered = [...chats];
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      filtered = filtered.filter((chat) => (
        chat.display_name?.toLowerCase().includes(query)
        || String(chat.chat_id).includes(query)
        || chat.last_message?.content?.toLowerCase().includes(query)
      ));
    }
    const stamp = (c) => (c.last_message?.date_create
      ? new Date(c.last_message.date_create).getTime()
      : new Date(c.date_create).getTime());
    // Закреплённые всегда сверху. Сервер уже отдаёт список в этом порядке,
    // но локальная сортировка нужна, чтобы чат прыгал наверх сразу после
    // нажатия «Закрепить», не дожидаясь перезагрузки списка.
    return filtered.sort((a, b) => {
      const pa = a.is_pinned ? 1 : 0;
      const pb = b.is_pinned ? 1 : 0;
      if (pa !== pb) return pb - pa;
      return stamp(b) - stamp(a);
    });
  }, [chats, searchQuery]);

  // ── Онлайн в списке чатов ───────────────────────────────────────────
  // Сводка «сколько в сети» отдельной ручкой: список грузится страницами и
  // кешируется, а статусы живут секунды — смешав их, мы получили бы список,
  // который нельзя кешировать, и статусы, которые врут.
  const [presenceMap, setPresenceMap] = useState({});
  const presenceTimerRef = useRef(null);

  // Ключ строим по ЗАГРУЖЕННЫМ чатам и сортируем по возрастанию. Не по
  // sortedChats: тот переставляется на каждое новое сообщение и сужается при
  // поиске — запрос статусов улетал бы на каждую реплику и на каждую букву.
  // Важен состав чатов, а не их порядок и не то, что сейчас видно.
  // Сервер отвечает максимум по 200 чатам. Если подгружено больше, режем
  // здесь и берём самые свежие (id растёт со временем), иначе обрезка на
  // сервере оставила бы статусы у самых старых чатов — ровно у тех, на
  // которые никто не смотрит.
  const chatIdsKey = useMemo(() => (chats || [])
    .map((c) => c.chat_id)
    .sort((a, b) => b - a)
    .slice(0, 200)
    .sort((a, b) => a - b)
    .join(','), [chats]);

  const loadPresence = useCallback(async () => {
    if (!tokenEmployee || !chatIdsKey) { setPresenceMap({}); return; }
    const ids = chatIdsKey.split(',').map(Number);
    const res = await APIChatsPresenceBulk(ids, tokenEmployee);
    if (res?.status === 'ok') setPresenceMap(res.chats || {});
  }, [tokenEmployee, chatIdsKey]);

  useEffect(() => { loadPresence(); }, [loadPresence]);

  // Событие о смене статуса приходит по сокету уведомлений (его рассылает
  // context.jsx). В нём один человек, а нам нужны счётчики по чатам —
  // поэтому не патчим карту на месте, а перезапрашиваем сводку. Вход и
  // выход случаются редко, зато всплеском: небольшая задержка склеивает
  // пачку событий в один запрос.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onPresence = () => {
      clearTimeout(presenceTimerRef.current);
      presenceTimerRef.current = setTimeout(loadPresence, 600);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') loadPresence();
    };
    window.addEventListener('planium:presence', onPresence);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(presenceTimerRef.current);
      window.removeEventListener('planium:presence', onPresence);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [loadPresence]);

  const matchedContacts = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return [];
    return contacts.filter((c) => {
      const fullName = `${c.last_name || ''} ${c.name || ''}`.toLowerCase();
      const nameRev = `${c.name || ''} ${c.last_name || ''}`.toLowerCase();
      return fullName.includes(q) || nameRev.includes(q);
    });
  }, [contacts, searchQuery]);


  const refreshChatsQuiet = async (folderId = activeFolder, { syncUnread = true } = {}) => {
    const session = tokenEmployee;
    const revision = ++chatRefreshRef.current;
    try {
      const data = await APIGetChats(tokenEmployee, {
        limit: CHATS_PAGE, offset: 0, folder: folderId,
      });
      if (folderTokenRef.current !== session || revision !== chatRefreshRef.current) return;
      // Кэшируем только заведомо правильный ответ. Ответ неожиданной формы
      // (например, тело ошибки с кодом 200) дал бы пустой список, который
      // потом мгновенно показывался бы из кэша при каждом заходе в папку.
      if (!Array.isArray(data?.chats)) {
        console.error('Список чатов пришёл в неожиданном виде:', data);
        return;
      }
      const list = data.chats;
      const snapshot = {
        list,
        offset: list.length,
        total: data?.total ?? list.length,
        hasMore: data?.has_more ?? false,
      };
      folderCacheRef.current.set(folderKey(folderId), snapshot);

      // Пока ходили на сервер, человек мог уйти в другую папку. Подменять
      // ей чужой список нельзя — именно так и появляются «не те чаты».
      if (folderKey(folderId) !== folderKey(activeFolderRef.current)) return;

      setChats(list);
      setChatsOffset(snapshot.offset);
      setChatsTotal(snapshot.total);
      setHasMoreChats(snapshot.hasMore);
      if (syncUnread) { loadsUnreadCounts(); loadContacts(); }
    } catch (error) {
      console.error('Ошибка обновления списка чатов:', error);
    }
  };

  // ── Переключение папки ─────────────────────────────────────────────
  const selectFolder = async (id) => {
    if (String(id ?? 'all') === String(activeFolder ?? 'all')) return;
    setActiveFolder(id);
    activeFolderRef.current = id;

    const cached = folderCacheRef.current.get(folderKey(id));
    if (cached) {
      // Есть что показать — показываем немедленно и не зажигаем спиннер.
      setChats(cached.list);
      setChatsOffset(cached.offset);
      setChatsTotal(cached.total);
      setHasMoreChats(cached.hasMore);
      setChatsLoading(false);
      refreshChatsQuiet(id);          // молча освежаем в фоне
      return;
    }

    setChatsLoading(true);
    await refreshChatsQuiet(id);
    if (folderKey(id) === folderKey(activeFolderRef.current)) setChatsLoading(false);
  };

  // ── Действия из контекстного меню чата ─────────────────────────────
  const handleTogglePin = async (chat, pinned) => {
    // Показываем сразу: ответ сервера тут ничего не добавляет, а ожидание
    // видно глазом — чат должен прыгнуть наверх мгновенно.
    setChats((prev) => prev.map((c) => (
      c.chat_id === chat.chat_id ? { ...c, is_pinned: pinned } : c
    )));
    const res = await APIToggleChatPin(chat.chat_id, pinned, tokenEmployee);
    if (res?.status !== 'ok') {
      setChats((prev) => prev.map((c) => (
        c.chat_id === chat.chat_id ? { ...c, is_pinned: !pinned } : c
      )));
    }
  };

  const handleLeaveChat = async (chat) => {
    const res = await APILeaveChat(chat.chat_id, tokenEmployee);
    if (res?.status === 'ok') {
      setChats((prev) => prev.filter((c) => c.chat_id !== chat.chat_id));
      loadFolders();
    } else {
      alert(res?.detail || res?.error || 'Не удалось покинуть чат');
    }
  };

  const handleMarkChatRead = async (chat) => {
    setChats((prev) => prev.map((c) => (
      c.chat_id === chat.chat_id ? { ...c, unread_count: 0 } : c
    )));
    const response = await APIMarkChatReadAll(chat.chat_id, tokenEmployee);
    if (response?.status === 'ok' && activeFolderRef.current != null) {
      folderCacheRef.current.delete(folderKey(activeFolderRef.current));
      await refreshChatsQuiet(activeFolderRef.current, { syncUnread: false });
    }
    loadsUnreadCounts();
    loadFolders();
  };

  // ── Папки: создание, правка, удаление, состав ──────────────────────
  const createFolder = async (payload) => {
    setFolderBusy(true);
    const res = await APICreateChatFolder(payload, tokenEmployee);
    setFolderBusy(false);
    if (res?.status === 'ok') await loadFolders();
    return res;
  };

  const updateFolder = async (id, payload) => {
    setFolderBusy(true);
    const res = await APIUpdateChatFolder(id, payload, tokenEmployee);
    setFolderBusy(false);
    if (res?.status === 'ok') await loadFolders();
    return res;
  };

  const reorderFolders = async (ids) => {
    if (folderBusy) return { error: 'Дождитесь сохранения папок' };
    const session = tokenEmployee;
    setFolderBusy(true);
    try {
      const result = await APIReorderChatFolders(ids, session);
      if (folderTokenRef.current !== session) return { error: 'Аккаунт изменился' };
      if (result?.status === 'ok') {
        const byId = new Map(foldersRef.current.map(folder => [folder.id, folder]));
        const ordered = ids.map(id => byId.get(id)).filter(Boolean);
        foldersRef.current = ordered;
        setFolders(ordered);
      }
      await loadFolders();
      return result;
    } finally { if (folderTokenRef.current === session) setFolderBusy(false); }
  };

  const deleteFolder = async (id) => {
    setFolderBusy(true);
    const res = await APIDeleteChatFolder(id, tokenEmployee);
    setFolderBusy(false);
    if (res?.status === 'ok') {
      if (String(activeFolder) === String(id)) await selectFolder(null);
      await loadFolders();
    }
    return res;
  };

  const toggleChatInFolder = async (folderId, chatId, action) => {
    setFolderBusy(true);
    const res = await APIFolderToggleChat(folderId, chatId, action, tokenEmployee);
    setFolderBusy(false);
    if (res?.status === 'ok') {
      await loadFolders();
      if (activeFolder != null) await refreshChatsQuiet(activeFolder);
    }
    return res;
  };

  const handleContactAction = async (contact) => {
    // React state updates on the next render; a ref also covers two rapid taps.
    if (openingContactRef.current) return;
    openingContactRef.current = true;
    try {
      setBusyContactId(contact.id);
      let targetChatId;
      if (contact.has_dm && contact.chat_id) {
        targetChatId = contact.chat_id;
      } else {
        const response = await APICreateChatWithEmployee(contact.id, tokenEmployee);
        targetChatId = response?.chat_id;
        if (!targetChatId) throw new Error('Ответ create/ без chat_id');
      }
      setContacts(prev => prev.map(row => String(row.id) === String(contact.id)
        ? { ...row, has_dm: true, chat_id: targetChatId } : row));
      await refreshChatsQuiet();
      if (onNewChatCreated) onNewChatCreated(targetChatId);
      setSearchQuery('');
      setContactsModalOpen(false);
    } catch (error) {
      console.error('Ошибка открытия личного чата:', error);
      alert('Не удалось открыть личный чат');
    } finally {
      openingContactRef.current = false;
      setBusyContactId(null);
    }
  };

  const handleGroupCreated = async (chatId) => {
    await refreshChatsQuiet();
    if (onNewChatCreated) onNewChatCreated(chatId);
    setGroupModalOpen(false);
    setSearchQuery('');
  };

  const showContactsSection = searchQuery.trim() && matchedContacts.length > 0;

  const SectionLabel = ({ children }) => (
    <Typography
      level="body-xs"
      sx={{
        px: 2, pt: 1.25, pb: 0.5,
        color: 'text.tertiary', fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 10.5,
      }}
    >
      {children}
    </Typography>
  );

  return (
    <Sheet
      sx={(theme) => ({
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid',
        borderColor: 'divider',
        // Высоту задаёт страница (см. useFillHeight в MessagesPage)
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
        backgroundColor: theme.palette.mode === 'dark' ? '#141823' : '#fff',
      })}
    >
      {/* Шапка: заголовок и действия иконками — две широкие кнопки съедали
          место, из-за которого список и выглядел перегруженным */}
      <Stack
        direction="row"
        spacing={0.5}
        sx={{
          alignItems: 'center', px: 2, pt: 1.75, pb: 1,
          // space-between + minWidth:0 у заголовка. Раньше кнопки держались
          // на mr:'auto' у Typography — и липли к счётчику: у заголовка своя
          // ширина по содержимому, свободное место доставалось не ему.
          justifyContent: 'space-between',
        }}
      >
        <Typography level="h4" sx={{ fontWeight: 800, fontSize: 21, minWidth: 0 }}>
          Чаты
          <Box
            component="span"
            sx={{
              ml: 1, px: 0.875, py: 0.125,
              fontSize: 11.5, fontWeight: 700,
              borderRadius: '8px', verticalAlign: 'middle',
              backgroundColor: 'rgba(99,102,241,0.12)', color: 'primary.500',
            }}
          >
            {chatsTotal || chats?.length || 0}
          </Box>
        </Typography>

        {/* Группа кнопок. Отдельным флекс-элементом: так она целиком
            прижимается к правому краю, а не «едет» за заголовком. */}
        <Stack
          direction="row"
          spacing={0.25}
          sx={{ alignItems: 'center', flexShrink: 0, ml: 'auto' }}
        >
        <Tooltip title="Папки чатов" size="sm">
          <IconButton
            size="sm" variant="plain" color="neutral"
            onClick={() => setFolderModal({ open: true, chat: null })}
            sx={(theme) => ({
              borderRadius: '50%',
              ...iconColor(theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.58)' : 'rgba(71,84,103,0.68)'),
              '&:hover': {
                backgroundColor: 'background.level1',
                ...iconColor('var(--joy-palette-primary-500)'),
              },
            })}
          >
            <CreateNewFolderRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Создать группу" size="sm">
          <IconButton
            size="sm" variant="plain" color="neutral"
            onClick={() => setGroupModalOpen(true)}
            sx={(theme) => ({
              borderRadius: '50%',
              ...iconColor(theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.58)' : 'rgba(71,84,103,0.68)'),
              '&:hover': {
                backgroundColor: 'background.level1',
                ...iconColor('var(--joy-palette-primary-500)'),
              },
            })}
          >
            <GroupAddRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Контакты" size="sm">
          <IconButton
            size="sm" variant="plain" color="neutral"
            onClick={() => setContactsModalOpen(true)}
            sx={(theme) => ({
              borderRadius: '50%',
              ...iconColor(theme.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.58)' : 'rgba(71,84,103,0.68)'),
              '&:hover': {
                backgroundColor: 'background.level1',
                ...iconColor('var(--joy-palette-primary-500)'),
              },
            })}
          >
            <PersonAddAltRoundedIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
        </Stack>
      </Stack>

      {/* Поиск */}
      <Box sx={{ px: 1.75, pb: 1 }}>
        <Input
          size="sm"
          placeholder="Поиск по чатам и людям…"
          startDecorator={<SearchRoundedIcon sx={{ fontSize: 18, color: 'text.tertiary' }} />}
          endDecorator={searchQuery && (
            <IconButton
              variant="plain" size="sm" onClick={() => setSearchQuery('')}
              sx={{ borderRadius: '50%', ...iconColor('var(--joy-palette-text-tertiary)') }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          )}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            borderRadius: '14px',
            backgroundColor: 'background.level1',
            '--Input-focusedThickness': '1.5px',
            // Та же причина, что и в поле ввода сообщения: шрифт меньше 16px
            // заставляет Safari на iOS приближать страницу при фокусе.
            '& input': { fontSize: { xs: 16, sm: 14 } },
          }}
        />
      </Box>

      <ContactsModal
        open={contactsModalOpen}
        onClose={() => setContactsModalOpen(false)}
        contacts={contacts}
        loading={contactsLoading}
        busyId={busyContactId}
        onAction={handleContactAction}
      />

      <CreateGroupModal
        open={groupModalOpen}
        onClose={() => setGroupModalOpen(false)}
        contacts={contacts}
        loading={contactsLoading}
        token={tokenEmployee}
        onCreated={handleGroupCreated}
      />

      <FolderStrip
        folders={folders}
        activeId={activeFolder}
        allUnread={allUnread}
        onSelect={selectFolder}
        onManage={() => setFolderModal({ open: true, chat: null })}
      />

      {/* Прокручиваемая часть */}
      <Box
        onScroll={handleChatsScroll}
        sx={(theme) => ({
          flex: 1, minHeight: 0, minWidth: 0,
          overflowY: 'auto',
          // Горизонтали тут не место: длинное название чата обязано
          // обрезаться эллипсисом, а не растягивать колонку. Именно эта
          // полоса внизу панели и выдала, что список не исчез, а стоит
          // сдвинутым и невидимым.
          overflowX: 'hidden',
          pb: 1,
          ...thinScrollbar(theme),
        })}
      >
        <AnimatePresence initial={false}>
          {showContactsSection && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: MOTION.base, ease: MOTION.ease }}
            >
              <SectionLabel>Контакты</SectionLabel>
              <List sx={{ py: 0, px: 0.75, '--List-gap': '2px' }}>
                {matchedContacts.map((c) => (
                  <ContactRow key={c.id} contact={c} busy={busyContactId === c.id} onAction={handleContactAction} />
                ))}
              </List>
              <SectionLabel>Чаты</SectionLabel>
            </motion.div>
          )}
        </AnimatePresence>

        {(
          <>
            {/* Смены папки БЕЗ AnimatePresence — намеренно, с третьей попытки.
                Съезжающая лента дважды приводила сюда с одним и тем же
                симптомом: перешёл в папку, а там пусто. Разгадка — в том,
                КАК именно пусто: список не исчезал, он оставался в DOM с
                initial-состоянием opacity 0 и сдвигом 24px, которое так и
                не сменилось на animate. Отсюда и пустая панель при верном
                счётчике «Чаты 1», и возможность её листать, и горизонтальная
                полоса прокрутки ровно в те самые 24 пикселя.

                Чинить это дальше настройками mode/key — гадание. Поэтому
                контейнер больше не анимируется вовсе и не перемонтируется:
                он всегда один и тот же узел. Появление новой папки рисуют
                сами строки — у ChatListItem своя анимация со сдвигом по
                индексу, и выглядит это не хуже, а сломаться там нечему.

                Спиннер — сосед, а не содержимое: он не трогает список. */}
            <Box sx={{ position: 'relative' }}>
            {sortedChats.map((chat, i) => (
              <Fragment key={`${activeFolder ?? 'all'}:${chat.chat_id}`}>
              {(i === 0 && chat.is_pinned || !chat.is_pinned && sortedChats[i - 1]?.is_pinned) && (
                <Stack direction="row" alignItems="center" spacing={0.75}
                  sx={{ px: 2, pt: i === 0 ? 0.75 : 1.25, pb: 0.5, color: 'text.tertiary' }}>
                  {chat.is_pinned && <PushPinRoundedIcon sx={{ fontSize: 12, color: 'primary.500' }} />}
                  <Typography level="body-xs" sx={{ fontSize: 10, fontWeight: 750, letterSpacing: '.05em',
                    textTransform: 'uppercase', color: chat.is_pinned ? 'primary.600' : 'text.tertiary' }}>
                    {chat.is_pinned ? 'Закреплённые' : 'Остальные чаты'}
                  </Typography>
                  <Box sx={{ height: '1px', flex: 1, bgcolor: 'divider' }} />
                </Stack>
              )}
              <ChatListItem
                // Папка в ключе — намеренно. У строки есть layout="position",
                // и при смене папки framer пытался доехать до нового места от
                // старого, померенного ещё в прошлом списке. Пока сверху жил
                // анимированный контейнер со сдвигом, эта проекция уезжала в
                // никуда: строки стояли за пределами панели, а панель
                // выглядела пустой, хотя прокручивалась и имела верный
                // счётчик. С папкой в ключе строки новой папки — новые узлы,
                // ехать им неоткуда. Внутри одной папки ключ прежний, так что
                // всплытие закреплённого чата наверх по-прежнему плавное.
                key={`${activeFolder ?? 'all'}:${chat.chat_id}`}
                chat={chat}
                index={i}
                onSelect={handleChatSelect}
                selected={selectedChatId === chat.chat_id}
                onContextMenu={(anchor, c) => setChatMenu({ anchor, chat: c })}
                presence={presenceMap[String(chat.chat_id)] || null}
              />
              </Fragment>
            ))}

            {!chatsLoading && !searchQuery.trim() && sortedChats.length === 0 && (
              <Stack alignItems="center" spacing={1} sx={{ py: 5, px: 3 }}>
                <ForumRoundedIcon sx={{ fontSize: 34, color: 'text.tertiary', opacity: 0.5 }} />
                <Typography level="body-sm" sx={{ textAlign: 'center', color: 'text.tertiary' }}>
                  {activeFolder ? 'В этой папке пока пусто' : 'Чатов пока нет'}
                </Typography>
              </Stack>
            )}

            <AnimatePresence>
              {chatsLoading && (
                <Box
                  component={motion.div}
                  key="chats-loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: MOTION.fast }}
                  sx={(theme) => ({
                    // Если список пуст — обычный блок с отступами; если в нём
                    // уже что-то есть (папку открывали раньше), накрываем его
                    // полупрозрачно, чтобы не мигать пустотой.
                    ...(sortedChats.length === 0
                      ? { py: 5 }
                      : {
                          position: 'absolute', inset: 0, zIndex: 2,
                          backgroundColor: theme.palette.mode === 'dark'
                            ? 'rgba(20,24,35,0.55)'
                            : 'rgba(255,255,255,0.55)',
                          backdropFilter: 'blur(1.5px)',
                          pt: 5,
                        }),
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', gap: 1.25,
                    pointerEvents: 'none',
                  })}
                >
                  <CircularProgress size="sm" />
                  <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>Загружаем чаты…</Typography>
                </Box>
              )}
            </AnimatePresence>
            </Box>

            {searchQuery.trim() && sortedChats.length === 0 && !showContactsSection && (
              <Stack alignItems="center" spacing={1} sx={{ py: 5, px: 3 }}>
                <ForumRoundedIcon sx={{ fontSize: 34, color: 'text.tertiary', opacity: 0.5 }} />
                <Typography level="body-sm" sx={{ textAlign: 'center', color: 'text.tertiary' }}>
                  Ничего не найдено
                </Typography>
              </Stack>
            )}

            {loadingMore && (
              <Stack sx={{ alignItems: 'center', py: 2 }}>
                <CircularProgress size="sm" />
              </Stack>
            )}
          </>
        )}
      </Box>

      {chatMenu && (
        <ChatContextMenu
          anchor={chatMenu.anchor}
          chat={chatMenu.chat}
          onTogglePin={handleTogglePin}
          onAddToFolder={(c) => setFolderModal({ open: true, chat: c })}
          onMarkRead={handleMarkChatRead}
          onLeave={handleLeaveChat}
          onClose={() => setChatMenu(null)}
        />
      )}

      <FolderModal
        open={folderModal.open}
        folders={folders}
        rules={folderRules}
        pickChat={folderModal.chat}
        busy={folderBusy}
        onCreate={createFolder}
        onUpdate={updateFolder}
        onDelete={deleteFolder}
        onToggleChat={toggleChatInFolder}
        onReorder={reorderFolders}
        onClose={() => setFolderModal({ open: false, chat: null })}
      />
    </Sheet>
  );
}
